/**
 * User profile management for billing and account info
 */

import { doc, getDoc, setDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';

const getDb = async () => db;
const PROFILE_STORAGE_PREFIX = 'user-profile:';

const getProfileStorageKey = (uid: string): string => `${PROFILE_STORAGE_PREFIX}${uid}`;

const loadLocalUserProfile = (uid: string): UserProfile | null => {
  if (typeof window === 'undefined' || !uid) return null;
  try {
    const raw = window.localStorage.getItem(getProfileStorageKey(uid));
    if (!raw) return null;
    return JSON.parse(raw) as UserProfile;
  } catch {
    return null;
  }
};

const saveLocalUserProfile = (profile: UserProfile): void => {
  if (typeof window === 'undefined' || !profile.uid) return;
  try {
    window.localStorage.setItem(getProfileStorageKey(profile.uid), JSON.stringify(profile));
  } catch {}
};

const isPermissionError = (err: unknown): boolean => {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return (
    message.includes('Missing or insufficient permissions') ||
    message.includes('permission-denied')
  );
};

let userProfileRemoteReadable = true;
let userProfileRemoteWarned = false;

const markUserProfileRemoteUnavailable = (): void => {
  userProfileRemoteReadable = false;
  if (!userProfileRemoteWarned) {
    userProfileRemoteWarned = true;
    console.warn('[userProfile] Firestore denied, using local profile fallback');
  }
};

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  // Optional roles assigned to the user (e.g. ['admin', 'viewer'])
  roles?: string[];
  // Legacy single-role field (optional)
  role?: string;
  // Billing info
  billingAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  // Payment method reference (never store actual card details)
  paymentMethodId?: string;
  // Subscription info
  subscriptionTier?: 'free' | 'beta' | 'premium';
  subscriptionStatus?: 'active' | 'inactive' | 'trialing' | 'canceled';
  preferredTheme?: string;
  // Profile completion
  profileComplete?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Load user profile from Firestore
 */
export async function loadUserProfile(uid: string): Promise<UserProfile | null> {
  const db = await getDb();
  if (!db || !userProfileRemoteReadable) return loadLocalUserProfile(uid);
  try {
    const docRef = doc(db, 'userProfiles', uid);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      const profile = snapshot.data() as UserProfile;
      saveLocalUserProfile(profile);
      return profile;
    }
    return loadLocalUserProfile(uid);
  } catch (err) {
    if (isPermissionError(err)) {
      markUserProfileRemoteUnavailable();
      return loadLocalUserProfile(uid);
    }
    console.error('Failed to load user profile:', err);
    return loadLocalUserProfile(uid);
  }
}

/**
 * Save user profile to Firestore
 */
export async function saveUserProfile(profile: UserProfile): Promise<void> {
  const db = await getDb();
  const nextProfile = {
    ...profile,
    updatedAt: new Date().toISOString(),
  };
  saveLocalUserProfile(nextProfile);
  if (!db) return;
  try {
    const docRef = doc(db, 'userProfiles', profile.uid);
    await setDoc(docRef, nextProfile, { merge: true });
  } catch (err) {
    if (isPermissionError(err)) {
      markUserProfileRemoteUnavailable();
      console.warn('Using local profile fallback because Firestore denied profile write');
      return;
    }
    console.error('Failed to save user profile:', err);
    throw err;
  }
}

/**
 * Subscribe to user profile changes
 */
export function subscribeUserProfile(
  uid: string,
  onData: (profile: UserProfile | null) => void,
  onError?: (error: Error) => void
): Unsubscribe | null {
  if (!db || !userProfileRemoteReadable) {
    onData(loadLocalUserProfile(uid));
    return null;
  }
  try {
    const docRef = doc(db, 'userProfiles', uid);
    return onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          onData(snapshot.data() as UserProfile);
        } else {
          onData(null);
        }
      },
      (error) => {
        if (isPermissionError(error)) {
          markUserProfileRemoteUnavailable();
        } else {
          console.error('Profile subscription error:', error);
        }
        const localProfile = loadLocalUserProfile(uid);
        if (localProfile) {
          onData(localProfile);
          return;
        }
        onError?.(error as Error);
      }
    );
  } catch (err) {
    if (isPermissionError(err)) {
      markUserProfileRemoteUnavailable();
    } else {
      console.error('Failed to subscribe to user profile:', err);
    }
    const localProfile = loadLocalUserProfile(uid);
    if (localProfile) {
      onData(localProfile);
      return null;
    }
    onError?.(err as Error);
    return null;
  }
}

/**
 * Check if user profile is complete (has required fields)
 */
export function isProfileComplete(profile: UserProfile | null): boolean {
  if (!profile) return false;
  return Boolean(
    profile.displayName &&
    profile.email &&
    profile.profileComplete
  );
}
